import React, { useEffect, useMemo, useState } from 'react';
import axiosInstance from '../../utils/axios'; 
import { 
  Autocomplete, 
  Box, 
  Button, 
  CircularProgress, 
  Dialog, 
  DialogContent, 
  IconButton,
  MenuItem, 
  Stack, 
  TextField, 
  Typography, 
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { matchIsValidTel, MuiTelInput } from 'mui-tel-input';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext/AuthContext';
import { updateProfile } from '../../pages/user/profil/services/put/profile/updateProfile';
import { ProfileInformations } from '../../pages/user/profil/services/put/profile/models';
import { ALLOWED_CHARACTERS_REGEX, ALLOWED_LENGTHS_REGEX, } from '../../utils/input_validation_utils';
import { isImpersonating } from '../../utils/impersonationSession';
import BirthdatePicker from '../BirthdatePicker/BirthdatePicker';
import { darkTheme } from '../../themes/darkTheme';

interface ProfileIncompleteModalProps {
  open: boolean;
  onClose: () => void;
  onSave?: () => void;
}

interface CountryItem {
  countryCode: string;
  commonName: string;
}

const ProfileIncompleteModal: React.FC<ProfileIncompleteModalProps> = ({
  open,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation('profileIncomplete');
  const { tokens, profile, refreshProfile } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [countriesList, setCountriesList] = useState<CountryItem[]>([]);
  const isAdminView = isImpersonating();

  const [formData, setFormData] = useState<
    ProfileInformations & { password: string }
  >({
    firstName: '',
    lastName: '',
    title: '',
    birthdate: '',
    phoneNumber: '',
    country: '',
    city: '',
    street: '',
    postalCode: '',
    password: '',
  });

  // Fetch allowed countries
  useEffect(() => {
    axiosInstance
      .get('/compliance/countries/allowed')
      .then((response) => {
        setCountriesList(response.data.data || []);
      })
      .catch((err) => console.error('Failed to fetch countries:', err));
  }, []);

  // Load profile data when modal opens
  useEffect(() => {
    if (profile && open) {
      setHasSubmitted(false);
      setFormData({
        firstName: profile.firstname || '',
        lastName: profile.lastname || '',
        title: '',
        birthdate: '',
        phoneNumber: profile.phoneNumber || '',
        country: profile.country || '',
        city: profile.city || '',
        street: profile.street || '',
        postalCode: profile.postalCode || '',
        password: '',
      });
    }
  }, [profile, open]);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // --- Name Validation ---
  const hasNameGotForbiddenCharacters = (value: string): boolean => {
    return !ALLOWED_CHARACTERS_REGEX.name.test(value);
  };

  const isNameTooLong = (value: string): boolean => {
    return !ALLOWED_LENGTHS_REGEX.name.test(value);
  };

  const isNameFieldIncorrect = (value: string): boolean => {
    if (!value?.trim()) return true;
    return hasNameGotForbiddenCharacters(value) || isNameTooLong(value);
  };

  const getNameHelperText = (value: string): string => {
    if (!value?.trim()) return t('ERRORS.REQUIRED');
    if (hasNameGotForbiddenCharacters(value))
      return t('ERRORS.FORBIDDEN_CHARACTERS');
    if (isNameTooLong(value)) return t('ERRORS.TOO_LONG');
    return '';
  };

  // --- City Validation ---
  const hasCityGotForbiddenCharacters = (value: string): boolean => {
    return !ALLOWED_CHARACTERS_REGEX.city.test(value);
  };

  const isCityTooLong = (value: string): boolean => {
    return !ALLOWED_LENGTHS_REGEX.city.test(value);
  };

  const isCityFieldIncorrect = (value: string): boolean => {
    if (!value?.trim()) return true;
    return isCityTooLong(value.trim()) || hasCityGotForbiddenCharacters(value);
  };

  const getCityHelperText = (value: string): string => {
    if (!value?.trim()) return t('ERRORS.REQUIRED');
    if (isCityTooLong(value.trim())) return t('ERRORS.TOO_LONG');
    if (hasCityGotForbiddenCharacters(value))
      return t('ERRORS.FORBIDDEN_CHARACTERS');
    return '';
  };

  // --- Street Validation ---
  const hasStreetGotForbiddenCharacters = (value: string): boolean => {
    return !ALLOWED_CHARACTERS_REGEX.street.test(value);
  };

  const isStreetTooLong = (value: string): boolean => {
    return !ALLOWED_LENGTHS_REGEX.street.test(value);
  };

  const isStreetFieldIncorrect = (value: string): boolean => {
    if (!value?.trim()) return true;
    return (
      isStreetTooLong(value.trim()) || hasStreetGotForbiddenCharacters(value)
    );
  };

  const getStreetHelperText = (value: string): string => {
    if (!value?.trim()) return t('ERRORS.REQUIRED');
    if (isStreetTooLong(value.trim())) return t('ERRORS.TOO_LONG');
    if (hasStreetGotForbiddenCharacters(value))
      return t('ERRORS.FORBIDDEN_CHARACTERS');
    return '';
  };

  // --- Zipcode Validation ---
  const isZipcodeInvalid = (value: string): boolean => {
    const zipNormalized = value.replace(/\s/g, '');
    return (
      !ALLOWED_CHARACTERS_REGEX.zipcode.test(zipNormalized) ||
      !ALLOWED_LENGTHS_REGEX.zipcode.test(zipNormalized)
    );
  };

  const isZipcodeFieldIncorrect = (value: string): boolean => {
    if (!value?.trim()) return true;
    return isZipcodeInvalid(value);
  };

  const getZipcodeHelperText = (value: string): string => {
    if (!value?.trim()) return t('ERRORS.REQUIRED');
    if (isZipcodeInvalid(value)) return t('ERRORS.INVALID_ZIPCODE');
    return '';
  };

  // --- Birthdate Validation ---
  const isBirthdateFieldIncorrect = (value: string): boolean => {
    return !value?.trim();
  };



  // --- Country Validation ---
  const isCountryFieldIncorrect = (value: string): boolean => {
    if (!value?.trim()) return true;
    // Vérifier si la valeur saisie fait partie de la liste des pays autorisés
    return !countriesList.some((item) => item.commonName === value);
  };

  // --- Phone Validation ---
  const isPhoneFieldIncorrect = (value: string): boolean => {
    if (!value?.trim()) return true;
    return !matchIsValidTel(value);
  };

  // --- Form Validation ---
  const isFormInvalid = useMemo(() => {
    return (
      isNameFieldIncorrect(formData.firstName || '') ||
      isNameFieldIncorrect(formData.lastName || '') ||
      isBirthdateFieldIncorrect(formData.birthdate || '') ||
      isCityFieldIncorrect(formData.city || '') ||
      isStreetFieldIncorrect(formData.street || '') ||
      isZipcodeFieldIncorrect(formData.postalCode || '') ||
      isCountryFieldIncorrect(formData.country || '') ||
      isPhoneFieldIncorrect(formData.phoneNumber || '')
    );
  }, [formData, countriesList]);

  const handleSaveClick = async () => {
    setHasSubmitted(true);
    if (isFormInvalid || !tokens?.accessToken) return;

    setIsSaving(true);
    try {
      const profileToUpdate: ProfileInformations = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        title: formData.title || null,
        birthdate: formData.birthdate || null,
        phoneNumber: formData.phoneNumber,
        country: formData.country,
        city: formData.city,
        street: formData.street,
        postalCode: formData.postalCode,
      };

      await updateProfile(tokens.accessToken, profileToUpdate);
      await refreshProfile();
      toast.success(t('SUCCESS_MESSAGE'));
      onSave?.();
      onClose();
    } catch (error) {
      console.error('Failed to update profile', error);
      toast.error(t('ERROR_MESSAGE'));
    } finally {
      setIsSaving(false);
    }
  };

  // Common styles for input fields
  const inputStyles = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#1E2025',
      borderRadius: '12px',
      color: '#FFFFFF',
      '& fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.1)',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.2)',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#3F51B5',
      },
      '&.Mui-error fieldset': {
        borderColor: '#f44336',
      },
    },
    '& .MuiInputBase-input::placeholder': {
      color: '#8E8E93',
      opacity: 1,
    },
    '& .MuiFormHelperText-root': {
      color: '#f44336',
    },
    // Fix pour l'icone du clear/dropdown dans l'Autocomplete
    '& .MuiAutocomplete-clearIndicator, & .MuiAutocomplete-popupIndicator': {
      color: '#8E8E93',
    },
  };

  return (
    // Rendered by AuthProvider, above ThemeProviderComponent: without this the modal
    // and its popups (date picker, autocomplete) fall back to the red root theme.
    <ThemeProvider theme={darkTheme}>
    <Dialog
      open={open}
      disableEscapeKeyDown
      onClose={(_, reason) => {
        // Prevent closing on backdrop click or escape key
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
      }}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          backgroundColor: '#121214',
          border: '1px solid #FFFFFF1A',
          borderRadius: '20px',
          padding: '24px',
          backgroundImage: 'none',
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Stack spacing={3}>
          {/* Admin close button */}
          {isAdminView && (
            <Button
              onClick={onClose}
              startIcon={<CloseIcon />}
              variant="outlined"
              size="small"
              sx={{
                alignSelf: 'flex-end',
                textTransform: 'none',
                borderColor: 'rgba(255, 255, 255, 0.2)',
                color: '#8E8E93',
                fontWeight: 600,
                borderRadius: '8px',
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.4)',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                },
              }}
            >
              {t('ADMIN_VIEW_ONLY')}
            </Button>
          )}

          {/* Header Section */}
          <Box textAlign="center">
            <Typography
              variant="h5"
              sx={{ color: '#FFFFFF', fontWeight: 700, mb: 1 }}
            >
              {t('TITLE')}
            </Typography>
            <Typography sx={{ color: '#8E8E93', fontSize: '14px' }}>
              {t('SUBTITLE')}
            </Typography>
          </Box>

          {/* Form Section */}
          <Stack spacing={2}>
            {/* Row 1: First Name, Last Name */}
            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                placeholder={t('PLACEHOLDERS.FIRST_NAME')}
                variant="outlined"
                value={formData.firstName || ''}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                error={
                  hasSubmitted && isNameFieldIncorrect(formData.firstName || '')
                }
                helperText={
                  hasSubmitted
                    ? getNameHelperText(formData.firstName || '')
                    : ''
                }
                sx={inputStyles}
              />
              <TextField
                fullWidth
                placeholder={t('PLACEHOLDERS.LAST_NAME')}
                variant="outlined"
                value={formData.lastName || ''}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                error={
                  hasSubmitted && isNameFieldIncorrect(formData.lastName || '')
                }
                helperText={
                  hasSubmitted ? getNameHelperText(formData.lastName || '') : ''
                }
                sx={inputStyles}
              />
            </Stack>

            {/* Row 2: Birthdate (full width) */}
            <BirthdatePicker
              dark
              value={formData.birthdate}
              onChange={(value) => handleInputChange('birthdate', value)}
              label={t('PLACEHOLDERS.BIRTHDATE')}
              error={
                hasSubmitted &&
                isBirthdateFieldIncorrect(formData.birthdate || '')
              }
              helperText={
                hasSubmitted &&
                isBirthdateFieldIncorrect(formData.birthdate || '')
                  ? t('ERRORS.REQUIRED')
                  : ''
              }
              sx={inputStyles}
            />

            {/* Row 3: Phone Number (full width) */}
            <MuiTelInput
              fullWidth
              placeholder={t('PLACEHOLDERS.PHONE_NUMBER')}
              value={formData.phoneNumber || ''}
              onChange={(value) => handleInputChange('phoneNumber', value)}
              error={
                hasSubmitted &&
                isPhoneFieldIncorrect(formData.phoneNumber || '')
              }
              helperText={
                hasSubmitted &&
                isPhoneFieldIncorrect(formData.phoneNumber || '')
                  ? t('ERRORS.INVALID_PHONE')
                  : ''
              }
              sx={inputStyles}
            />

            {/* Row 4: Street, Postal Code */}
            {/* Row 4: Street, Postal Code */}
            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                placeholder={t('PLACEHOLDERS.STREET')}
                variant="outlined"
                value={formData.street || ''}
                onChange={(e) => handleInputChange('street', e.target.value)}
                error={
                  hasSubmitted && isStreetFieldIncorrect(formData.street || '')
                }
                helperText={
                  hasSubmitted ? getStreetHelperText(formData.street || '') : ''
                }
                sx={inputStyles}
              />
              <TextField
                fullWidth
                placeholder={t('PLACEHOLDERS.POSTAL_CODE')}
                variant="outlined"
                value={formData.postalCode || ''}
                onChange={(e) =>
                  handleInputChange('postalCode', e.target.value)
                }
                error={
                  hasSubmitted &&
                  isZipcodeFieldIncorrect(formData.postalCode || '')
                }
                helperText={
                  hasSubmitted
                    ? getZipcodeHelperText(formData.postalCode || '')
                    : ''
                }
                sx={inputStyles}
              />
            </Stack>

            {/* Row 5: City, Country */}
            {/* Row 5: City, Country */}
            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                placeholder={t('PLACEHOLDERS.CITY')}
                variant="outlined"
                value={formData.city || ''}
                onChange={(e) => handleInputChange('city', e.target.value)}
                error={
                  hasSubmitted && isCityFieldIncorrect(formData.city || '')
                }
                helperText={
                  hasSubmitted ? getCityHelperText(formData.city || '') : ''
                }
                sx={inputStyles}
              />
              
              <Autocomplete
                fullWidth
                options={countriesList}
                getOptionLabel={(option) => option.commonName}
                value={
                  countriesList.find(
                    (item) => item.commonName === formData.country,
                  ) || null
                }
                onChange={(event, newValue) => {
                  handleInputChange('country', newValue ? newValue.commonName : '');
                }}
                renderOption={(props, option) => (
                  <MenuItem
                    {...props}
                    key={option.countryCode}
                    value={option.countryCode}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        component="img"
                        src={`https://flagcdn.com/w40/${option.countryCode.toLowerCase()}.png`}
                        alt={`Flag of ${option.commonName}`}
                        sx={{ width: 24, height: 16, borderRadius: '2px' }}
                      />
                      {option.commonName}
                    </Box>
                  </MenuItem>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={t('PLACEHOLDERS.COUNTRY')}
                    variant="outlined"
                    error={
                      hasSubmitted &&
                      isCountryFieldIncorrect(formData.country || '')
                    }
                    helperText={
                      hasSubmitted &&
                      isCountryFieldIncorrect(formData.country || '')
                        ? t('ERRORS.REQUIRED')
                        : ''
                    }
                    sx={inputStyles}
                  />
                )}
              />
            </Stack>
          </Stack>

          {/* Action Button */}
          <Button
            fullWidth
            variant="contained"
            onClick={handleSaveClick}
            disabled={isSaving}
            sx={{
              backgroundColor: '#3E52FF',
              color: '#FFFFFF',
              textTransform: 'none',
              borderRadius: '12px',
              py: 1.5,
              fontSize: '16px',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#3546E5',
              },
              '&:disabled': {
                backgroundColor: 'rgba(62, 82, 255, 0.5)',
                color: 'rgba(255, 255, 255, 0.5)',
              },
            }}
          >
            {isSaving ? (
              <CircularProgress size={24} sx={{ color: '#FFFFFF' }} />
            ) : (
              t('BUTTON_SAVE')
            )}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
    </ThemeProvider>
  );
};

export default ProfileIncompleteModal;